import { useObjectMemo } from "../../../hooks";
import { PERMISSION, useBusinessPermissions } from "../permissions";

const BULK_TEMPLATE_PERMISSIONS = [
  PERMISSION.BUSINESS.WRITE.BULK_PAYMENT_TEMPLATE,
  PERMISSION.BUSINESS.WRITE.ACH_PAYMENT_VIA_TEMPLATE,
  PERMISSION.BUSINESS.WRITE.ACH_PAYMENT_COLLECT_VIA_TEMPLATE,
];

type BulkTemplatePermissions = {
  canSendViaTemplate: boolean;
  canCollectViaTemplate: boolean;
  canMakePayment: boolean;
};

export const getTemplateFilter = (
  permissions: Pick<
    BulkTemplatePermissions,
    "canSendViaTemplate" | "canCollectViaTemplate"
  >,
): string | null => {
  if (permissions.canSendViaTemplate && permissions.canCollectViaTemplate) {
    return "credit,debit,mixed";
  }
  if (permissions.canSendViaTemplate) return "credit";
  if (permissions.canCollectViaTemplate) return "debit";
  return null;
};

export const isMakePaymentDisabled = (
  permissions: BulkTemplatePermissions,
  hasSendPayments: boolean,
  hasCollectPayments: boolean,
): boolean =>
  !permissions.canMakePayment ||
  (hasSendPayments && !permissions.canSendViaTemplate) ||
  (hasCollectPayments && !permissions.canCollectViaTemplate);

export const useBulkTemplatePermissions = () => {
  const [
    hasManageTemplatePermission,
    hasSendViaTemplatePermission,
    hasCollectViaTemplatePermission,
  ] = useBusinessPermissions(BULK_TEMPLATE_PERMISSIONS);

  return useObjectMemo({
    canEdit: hasManageTemplatePermission,
    canSendViaTemplate: hasSendViaTemplatePermission,
    canCollectViaTemplate: hasCollectViaTemplatePermission,
    canMakePayment:
      hasSendViaTemplatePermission || hasCollectViaTemplatePermission,
    canDuplicate: hasManageTemplatePermission,
    canArchive: hasManageTemplatePermission,
  });
};
